/**
 * Safe evaluator + BigQuery compiler for per-tenant KPI formulas stored in the
 * `kpis` table (`Kpi.formula`, e.g. `"Spend / conversions"`, `"Spend / impressions * 1000"`,
 * `"Creatives / SUM(\"Creatives\")"`). Ports Retool's client-side `evalKPI` WITHOUT
 * `eval`/`new Function`: a tokenizer + shunting-yard parser produces an AST that
 * `evalKpi` walks against an aggregated topic row (and cross-topic totals for `SUM`).
 *
 * Grammar (confirmed against packages/db/src/seed/kpis-seed-data.ts):
 *   expr   := term (('+' | '-') term)*
 *   term   := factor (('*' | '/') factor)*
 *   factor := number | column | 'SUM(' column ')' | '(' expr ')'
 * Column tokens are case-insensitive and map to the numeric field allowlist.
 * There is NO unary minus / negative literal in the grammar — a `-`/`+` in
 * operand position is a COMPILE error (never silently mis-parsed).
 *
 * Null is ABSORBING end-to-end: any binary operation with a `null` operand yields
 * `null` (never `0`/`NaN`), and division by zero / a non-finite result yields `null`.
 * So `SUM("Col") === 0` makes every dependent value (and the series avg) `null`.
 *
 * The evaluator is also used client-side for Overview chart bars (a stated
 * deviation from frontend/web/AGENTS.md — see AD-1679). The allowlist is the
 * injection boundary: `compileToBigQuery` only emits identifiers from this list.
 */

/** Numeric column tokens a formula may reference. */
export const COLUMN_FIELDS = [
  'spend',
  'sticked',
  'value',
  'conversions',
  'clicks',
  'impressions',
  'link_click',
  'video_play_action',
  'video_thruplay_actions',
  'likes',
  'custom_event',
  'comments',
  'post_engagements',
  'creatives',
  'ads',
  'frequency',
  'avg_watch_time',
] as const;

export type KpiColumnField = (typeof COLUMN_FIELDS)[number];

/**
 * A row of numeric aggregates. Missing keys evaluate as null.
 * Partial (not `Record<string, …>`) so structured rows like `OverviewChartRow` are
 * assignable — they have no string index signature.
 */
export type KpiNumericRow = Partial<Record<KpiColumnField, number | string | null | undefined>>;

/** Cross-topic summed totals — same numeric shape, used for `SUM("Col")`. */
export type OverviewColumnTotals = Record<KpiColumnField, number>;

export function emptyColumnTotals(): OverviewColumnTotals {
  return Object.fromEntries(COLUMN_FIELDS.map((f) => [f, 0])) as OverviewColumnTotals;
}

/** lowercased token → canonical field. */
const FIELD_BY_LOWER: ReadonlyMap<string, KpiColumnField> = new Map(
  COLUMN_FIELDS.map((f) => [f.toLowerCase(), f]),
);

// ─── AST ──────────────────────────────────────────────────────────────────────

type Ast =
  | { kind: 'num'; value: number }
  | { kind: 'col'; field: KpiColumnField }
  | { kind: 'sum'; field: KpiColumnField }
  | { kind: 'bin'; op: '+' | '-' | '*' | '/'; left: Ast; right: Ast };

/** Thrown by `compileKpiFormula` on any malformed/unknown-token formula. */
export class KpiFormulaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'KpiFormulaError';
  }
}

// ─── Tokenizer ──────────────────────────────────────────────────────────────

type Token =
  | { t: 'num'; value: number }
  | { t: 'col'; field: KpiColumnField }
  | { t: 'sum'; field: KpiColumnField }
  | { t: 'op'; op: '+' | '-' | '*' | '/' }
  | { t: 'lparen' }
  | { t: 'rparen' };

/** Resolve a raw column identifier (any case) to a field, or throw. */
function resolveColumn(raw: string): KpiColumnField {
  const field = FIELD_BY_LOWER.get(raw.trim().toLowerCase());
  if (!field) throw new KpiFormulaError(`Unknown column token "${raw}"`);
  return field;
}

function tokenize(formula: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const src = formula;

  while (i < src.length) {
    const ch = src[i]!;

    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }

    if (ch === '(') {
      tokens.push({ t: 'lparen' });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ t: 'rparen' });
      i++;
      continue;
    }
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      tokens.push({ t: 'op', op: ch });
      i++;
      continue;
    }

    // Numeric literal (integer or decimal). No leading sign — unary is unsupported.
    if (ch >= '0' && ch <= '9') {
      let j = i + 1;
      while (j < src.length && /[0-9.]/.test(src[j]!)) j++;
      const lexeme = src.slice(i, j);
      const value = Number(lexeme);
      if (!Number.isFinite(value)) throw new KpiFormulaError(`Invalid number "${lexeme}"`);
      tokens.push({ t: 'num', value });
      i = j;
      continue;
    }

    // Identifier — either SUM(...) or a bare column token.
    if (/[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < src.length && /[A-Za-z0-9_]/.test(src[j]!)) j++;
      const ident = src.slice(i, j);

      if (ident.toUpperCase() === 'SUM') {
        // Expect: SUM ( "col" | 'col' | col )
        let k = j;
        while (k < src.length && /\s/.test(src[k]!)) k++;
        if (src[k] !== '(') throw new KpiFormulaError('SUM must be followed by "("');
        k++;
        while (k < src.length && /\s/.test(src[k]!)) k++;
        let quote: string | null = null;
        if (src[k] === '"' || src[k] === "'") {
          quote = src[k]!;
          k++;
        }
        const start = k;
        while (k < src.length && /[A-Za-z0-9_]/.test(src[k]!)) k++;
        const colName = src.slice(start, k);
        if (!colName) throw new KpiFormulaError('SUM() requires a column name');
        if (quote) {
          if (src[k] !== quote) throw new KpiFormulaError('Unterminated quote in SUM()');
          k++;
        }
        while (k < src.length && /\s/.test(src[k]!)) k++;
        if (src[k] !== ')') throw new KpiFormulaError('SUM() missing closing ")"');
        k++;
        tokens.push({ t: 'sum', field: resolveColumn(colName) });
        i = k;
        continue;
      }

      tokens.push({ t: 'col', field: resolveColumn(ident) });
      i = j;
      continue;
    }

    throw new KpiFormulaError(`Unexpected character "${ch}" in formula`);
  }

  return tokens;
}

// ─── Parser (shunting-yard → AST) ──────────────────────────────────────────────

const PRECEDENCE: Record<'+' | '-' | '*' | '/', number> = { '+': 1, '-': 1, '*': 2, '/': 2 };

function applyOp(operands: Ast[], op: '+' | '-' | '*' | '/'): void {
  const right = operands.pop();
  const left = operands.pop();
  if (!left || !right) throw new KpiFormulaError(`Operator "${op}" is missing an operand`);
  operands.push({ kind: 'bin', op, left, right });
}

/**
 * Compile a KPI formula string to an AST. For raw metrics (`Kpi.formula === null`)
 * the caller passes the KPI's own column name (a single column token IS a valid
 * formula). Throws `KpiFormulaError` on unknown tokens, unary operators, or mismatched
 * parens so the caller can skip + report the KPI rather than emit a wrong value.
 */
export function compileKpiFormula(formula: string): Ast {
  const tokens = tokenize(formula);
  if (tokens.length === 0) throw new KpiFormulaError('Empty formula');

  const operands: Ast[] = [];
  const ops: Array<'+' | '-' | '*' | '/' | '('> = [];
  // True when the next token must be an operand (start, after '(', or after a binary op).
  let expectOperand = true;

  for (const tok of tokens) {
    switch (tok.t) {
      case 'num':
        if (!expectOperand) throw new KpiFormulaError('Missing operator before value');
        operands.push({ kind: 'num', value: tok.value });
        expectOperand = false;
        break;
      case 'col':
        if (!expectOperand) throw new KpiFormulaError('Missing operator before column');
        operands.push({ kind: 'col', field: tok.field });
        expectOperand = false;
        break;
      case 'sum':
        if (!expectOperand) throw new KpiFormulaError('Missing operator before SUM()');
        operands.push({ kind: 'sum', field: tok.field });
        expectOperand = false;
        break;
      case 'op':
        if (expectOperand) {
          throw new KpiFormulaError(`Unary operator "${tok.op}" is not supported`);
        }
        while (ops.length > 0) {
          const top = ops[ops.length - 1]!;
          if (top === '(' || PRECEDENCE[top] < PRECEDENCE[tok.op]) break;
          applyOp(operands, ops.pop() as '+' | '-' | '*' | '/');
        }
        ops.push(tok.op);
        expectOperand = true;
        break;
      case 'lparen':
        if (!expectOperand) throw new KpiFormulaError('Missing operator before "("');
        ops.push('(');
        expectOperand = true;
        break;
      case 'rparen': {
        if (expectOperand) throw new KpiFormulaError('Empty parentheses or dangling operator');
        let found = false;
        while (ops.length > 0) {
          const top = ops.pop()!;
          if (top === '(') {
            found = true;
            break;
          }
          applyOp(operands, top);
        }
        if (!found) throw new KpiFormulaError('Mismatched parentheses');
        expectOperand = false;
        break;
      }
    }
  }

  while (ops.length > 0) {
    const top = ops.pop()!;
    if (top === '(') throw new KpiFormulaError('Mismatched parentheses');
    applyOp(operands, top);
  }

  if (operands.length !== 1) throw new KpiFormulaError('Malformed formula');
  return operands[0]!;
}

/** A compiled formula — the opaque AST returned by `compileKpiFormula`. */
export type CompiledKpiFormula = Ast;

/**
 * Every column token an expression reads, de-duplicated.
 *
 * For callers whose row is narrower than `COLUMN_FIELDS`: the Overview chart row
 * carries 16 of the 17 fields (it has no `avg_watch_time`), and a missing key
 * evaluates to `null`, which nulls the whole expression. Without this, such a KPI
 * renders as an empty bar with no explanation. `SUM()` tokens count too — they
 * read the same column, just from the totals row.
 */
export function collectColumnFields(ast: CompiledKpiFormula): KpiColumnField[] {
  const found = new Set<KpiColumnField>();
  const walk = (node: Ast): void => {
    switch (node.kind) {
      case 'num':
        return;
      case 'col':
      case 'sum':
        found.add(node.field);
        return;
      case 'bin':
        walk(node.left);
        walk(node.right);
    }
  };
  walk(ast);
  return [...found];
}

// ─── Evaluation ────────────────────────────────────────────────────────────────

function readNumeric(row: KpiNumericRow, field: KpiColumnField): number | null {
  const n = Number(row[field]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Evaluate a compiled formula for one topic row. `null` is absorbing: any null
 * operand, divide-by-zero, or non-finite result propagates `null`.
 */
export function evalKpi(
  ast: CompiledKpiFormula,
  row: KpiNumericRow,
  totals: KpiNumericRow,
): number | null {
  switch (ast.kind) {
    case 'num':
      return ast.value;
    case 'col':
      return readNumeric(row, ast.field);
    case 'sum':
      return readNumeric(totals, ast.field);
    case 'bin': {
      const l = evalKpi(ast.left, row, totals);
      const r = evalKpi(ast.right, row, totals);
      if (l === null || r === null) return null;
      let out: number;
      switch (ast.op) {
        case '+':
          out = l + r;
          break;
        case '-':
          out = l - r;
          break;
        case '*':
          out = l * r;
          break;
        case '/':
          if (r === 0) return null;
          out = l / r;
          break;
      }
      return Number.isFinite(out) ? out : null;
    }
  }
}

// ─── BigQuery compiler ─────────────────────────────────────────────────────────

export interface CompileToBigQueryOptions {
  /** Table / CTE alias prefix for column refs. Default `ads` (drilldown My Brand). */
  tableAlias?: string;
}

function quoteIdent(alias: string): string {
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(alias)) {
    throw new KpiFormulaError(`Unsafe BigQuery table alias "${alias}"`);
  }
  return alias;
}

function emitBq(ast: Ast, tableAlias: string): string {
  switch (ast.kind) {
    case 'num':
      return String(ast.value);
    case 'col':
      return `${tableAlias}.${ast.field}`;
    case 'sum':
      // Share-of-total: window over the grouped result set (drilldown outer SELECT).
      return `SUM(${tableAlias}.${ast.field}) OVER()`;
    case 'bin': {
      const left = emitBq(ast.left, tableAlias);
      const right = emitBq(ast.right, tableAlias);
      if (ast.op === '/') return `SAFE_DIVIDE(${left}, ${right})`;
      return `(${left} ${ast.op} ${right})`;
    }
  }
}

/**
 * Compile a KPI formula to a BigQuery expression. Only allowlisted column tokens
 * are emitted, so a user-authored formula cannot inject SQL. Division uses
 * `SAFE_DIVIDE` (NULL on a zero denominator, matching `evalKpi`).
 */
export function compileToBigQuery(
  formula: string,
  options: CompileToBigQueryOptions = {},
): string {
  const tableAlias = quoteIdent(options.tableAlias ?? 'ads');
  return emitBq(compileKpiFormula(formula), tableAlias);
}
